import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { orderBy } from 'lodash';
import { mergeDraftRows } from '../costing-template.helpers';
import type { TitleErrorEntry } from '../costing-template.helpers';
import type { CostingTemplateColumn, CostingTemplateDetail } from '../store/costingTemplate.types';

/** The column-editor's working copy of one costing template, saved rows and drafts mixed. */
export interface CostingTemplateEditorState {
  additionalRow: CostingTemplateColumn[];
  landedInfoRow: CostingTemplateColumn[];
  additionalError: TitleErrorEntry[];
  landedInfoError: TitleErrorEntry[];
  landedInfo: CostingTemplateColumn[];
  topColumnArr: CostingTemplateColumn[];
  defaultColumns: CostingTemplateColumn[];
  bottomColumnArr: CostingTemplateColumn[];
  additionalComponentRow: CostingTemplateColumn[];
  additionalComponentRowError: TitleErrorEntry[];
  costingInfoRow: CostingTemplateColumn[];
  costingInfoError: TitleErrorEntry[];
  specificationRequirementRow: CostingTemplateColumn[];
  specificationRequirementError: TitleErrorEntry[];
}

export interface CostingTemplateContextValue {
  costingTemplateState: CostingTemplateEditorState;
  setCostingTemplateState: React.Dispatch<React.SetStateAction<CostingTemplateEditorState>>;
}

/** The redux slice this provider reads. */
interface CostingTemplateProviderRootState {
  CostingTemplateData: { costingTemplateDetail?: CostingTemplateDetail | null };
}

export const StateContext = createContext<CostingTemplateContextValue | null>(null);

const initialSetting: CostingTemplateEditorState = {
  additionalRow: [],
  landedInfoRow: [],
  additionalError: [],
  landedInfoError: [],
  landedInfo: [],
  topColumnArr: [],
  defaultColumns: [],
  bottomColumnArr: [],
  additionalComponentRow: [],
  additionalComponentRowError: [],
  costingInfoRow: [],
  costingInfoError: [],
  specificationRequirementRow: [],
  specificationRequirementError: [],
};

/**
 * Holds the column editor's working copy of one costing template.
 *
 * The screen edits saved rows and unsaved drafts side by side, so it cannot render redux directly:
 * a refetch would wipe whatever the user had half-typed. This provider syncs the saved rows in from
 * `costingTemplateDetail` and hands them to `mergeDraftRows`, which keeps a draft until the server
 * echoes a row with the same title.
 *
 * Landed and costing-info rows are sorted by `position` on the way in — that is the order the drag
 * handle persists, and the server does not guarantee it.
 */
export default function CostingTemplateProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const costingTemReduxData = useSelector((state: CostingTemplateProviderRootState) => state.CostingTemplateData);
  const { costingTemplateDetail } = costingTemReduxData;

  const [costingTemplateState, setCostingTemplateState] = useState<CostingTemplateEditorState>(initialSetting);

  useEffect(() => {
    const newState: Partial<CostingTemplateEditorState> = {};

    const hasLandedInfo = Array.isArray(costingTemplateDetail?.landedInfo);
    if (hasLandedInfo) {
      newState.landedInfo = orderBy(costingTemplateDetail?.landedInfo, ['position'], ['asc']) as CostingTemplateColumn[];
    }

    if (Array.isArray(costingTemplateDetail?.topColumnArr)) {
      newState.topColumnArr = costingTemplateDetail?.topColumnArr as CostingTemplateColumn[];
    }

    if (Array.isArray(costingTemplateDetail?.defaultColumns)) {
      newState.defaultColumns = costingTemplateDetail?.defaultColumns as CostingTemplateColumn[];
    }

    if (Array.isArray(costingTemplateDetail?.bottomColumnArr)) {
      newState.bottomColumnArr = costingTemplateDetail?.bottomColumnArr as CostingTemplateColumn[];
    }

    const hasCostingInfo = Array.isArray(costingTemplateDetail?.costingInfo);

    if (Object.keys(newState).length > 0 || hasCostingInfo || hasLandedInfo) {
      setCostingTemplateState((prev) => {
        const merged: CostingTemplateEditorState = { ...prev, ...newState };
        if (hasLandedInfo) {
          merged.landedInfoRow = mergeDraftRows(newState.landedInfo, prev.landedInfoRow);
        }
        if (hasCostingInfo) {
          const sorted = orderBy(costingTemplateDetail?.costingInfo, ['position'], ['asc']) as CostingTemplateColumn[];
          merged.costingInfoRow = mergeDraftRows(sorted, prev.costingInfoRow);
        }
        return merged;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    costingTemplateDetail?.landedInfo,
    costingTemplateDetail?.topColumnArr,
    costingTemplateDetail?.defaultColumns,
    costingTemplateDetail?.bottomColumnArr,
    costingTemplateDetail?.costingInfo,
  ]);

  const contextValue = useMemo<CostingTemplateContextValue>(
    () => ({
      costingTemplateState,
      setCostingTemplateState,
    }),
    [costingTemplateState, setCostingTemplateState],
  );

  return <StateContext.Provider value={contextValue}>{children}</StateContext.Provider>;
}

/**
 * Reads the column-editor state.
 * @returns the editor state and its setter.
 */
export function useCostingTemplateState(): CostingTemplateContextValue {
  const context = useContext(StateContext);
  if (!context) {
    throw new Error('useCostingTemplateState must be used within a CostingTemplateProvider');
  }
  return context;
}
